import { useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { trackTreasureHuntAppear, trackTreasureHuntClick } from '../analytics';
import type { TreasureHuntIcon } from '../types/api';

import { DROP_VISIBLE_MS, pickDrop } from './drop';
import { useTreasureHunt } from './TreasureHuntProvider';

/**
 * Maybe shows an icon to catch, for a short while. Rolls once per mount, so the
 * stand page keys it by stand and tab: every tab opened is another chance, as
 * in the legacy app.
 */
export function TreasureDrop({
  icons,
  standId,
}: {
  icons: readonly TreasureHuntIcon[];
  standId: number;
}): ReactElement | null {
  const { t } = useTranslation();
  const { claim } = useTreasureHunt();
  const [icon, setIcon] = useState(() => pickDrop(icons));

  useEffect(() => {
    if (icon === undefined) {
      return undefined;
    }

    trackTreasureHuntAppear();
    const timer = window.setTimeout(() => {
      setIcon(undefined);
    }, DROP_VISIBLE_MS);

    return (): void => {
      window.clearTimeout(timer);
    };
  }, [icon]);

  if (icon === undefined) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => {
        trackTreasureHuntClick();
        claim({ icon, standId });
        setIcon(undefined);
      }}
      aria-label={t('treasureHunt.catch')}
      className="fixed right-5 bottom-5 z-40 size-[75px] rounded-full motion-safe:animate-treasure-pulse"
    >
      <img src={icon.imageUrl} alt="" className="size-full" />
    </button>
  );
}
